import ScreenWrapper from '@components/layout/screen-wrapper';
import { BrandLogo, Button, Field, Input, Stack, Text } from '@components/ui';
import OtpVerification from '@features/auth/otp-verification';
import { useOtpCooldown } from '@features/auth/otpCooldown';
import { phoneDigits, toUsPhoneNumber } from '@features/auth/phoneNumber';
import {
  type GetOtpFormValues,
  getOtpSchema,
} from '@features/auth/schemas/getOtpSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiErrorMessage } from '@lib/api/apiError';
import { useSendOtpMutation } from '@lib/api/auth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import type { AuthLocationState, UserRole } from '@typings/auth';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AuthSplitCard } from './auth-split-card';


const authPageSx = {
  height: '100dvh',
  maxHeight: '100dvh',
  width: '100%',
  overflow: 'auto',
};

const authContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  height: 'auto',
  width: '100%',
};

const GetOtp = (): JSX.Element => {
  const { t } = useTranslation();
  const ebayUrl = new URL(window.location.href).searchParams;
  const itemId = ebayUrl.get('itemID');
  const location = useLocation();
  const navigator = useNavigate();
  const sendOtp = useSendOtpMutation();
  const [showOtpVerificationModal, setShowOtpVerificationModal] =
    useState(false);
  const userRole =
    (location.state as AuthLocationState | undefined)?.userRole ??
    (localStorage.getItem('userRole') as UserRole | null) ??
    'buyer';

  const handleShowOtpVerificationModal = (): void => {
    setShowOtpVerificationModal(!showOtpVerificationModal);
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted, isValid },
  } = useForm<GetOtpFormValues>({
    defaultValues: {
      phoneNumber: '',
    },
    resolver: zodResolver(getOtpSchema),
    mode: 'onBlur',
  });

  const phoneNumber = watch('phoneNumber');
  const cooldown = useOtpCooldown(toUsPhoneNumber(phoneNumber));

  const onSubmit = (values: GetOtpFormValues): void => {
    if (cooldown.coolingDown) {
      setShowOtpVerificationModal(true);
      return;
    }
    const fullPhoneNumber = toUsPhoneNumber(values.phoneNumber);
    sendOtp
      .mutateAsync({
        phoneNumber: fullPhoneNumber,
        userRole,
        forgetPasswordOTP: true,
      })
      .then(() => {
        cooldown.start();
        setShowOtpVerificationModal(true);
      })
      .catch((error: { response?: { data?: { message?: string } } }) => {
        toast.error(
          apiErrorMessage(error) || 'Something went wrong'
        );
      });
  };

  const showPhoneError =
    (isSubmitted || errors.phoneNumber) && errors.phoneNumber;

  return (
    <ScreenWrapper pageSx={authPageSx} containerSx={authContainerSx}>
      <Box
        sx={{
          m: 'auto',
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          py: 3,
        }}
      >
        <AuthSplitCard>
          <BrandLogo alt={t('common.logoAlt')} height={48} sx={{ mx: 'auto', mb: 1.5 }} />
          <Text
            variant="subtitle"
            align="center"
            sx={{ mb: 1, fontWeight: 600, display: { md: 'none' } }}
          >
            {t('auth.tagline')}
          </Text>
          <Text
            variant="body"
            tone="muted"
            align="center"
            sx={{ mb: 3.5, fontSize: '0.875rem' }}
          >
            {t('auth.otpHint')}
          </Text>

          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}
          >
            <Field
              sx={{ mb: 2.5 }}
              label={t('auth.mobileNumber')}
              error={
                showPhoneError
                  ? t(errors.phoneNumber?.message ?? '')
                  : undefined
              }
            >
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <Input
                    type="tel"
                    id="phone-number"
                    invalid={Boolean(showPhoneError)}
                    placeholder={t('common.phonePlaceholder')}
                    inputProps={{ maxLength: 10 }}
                    value={phoneDigits(field.value)}
                    onChange={(event) => {
                      field.onChange(phoneDigits(event.target.value));
                    }}
                    onBlur={field.onBlur}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {t('common.countryCode')}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={!isValid}
              loading={sendOtp.isPending}
            >
              {cooldown.coolingDown ? t('auth.enterCode') : 'Send OTP'}
            </Button>

            <Stack
              align="stretch"
              gap="md"
              sx={{ mt: 'auto', pt: 5, width: '100%', textAlign: 'center' }}
            >
              <Text
                variant="body"
                tone="muted"
                sx={{
                  fontSize: '0.875rem',
                  '& strong': { color: 'text.primary' },
                }}
              >
                <Trans
                  i18nKey="auth.rememberPrompt"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Button
                type="button"
                variant="outlined"
                fullWidth
                endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                onClick={() =>
                  navigator(itemId ? `/login?itemID=${itemId}` : '/login')
                }
              >
                {t('common.signIn')}
              </Button>
            </Stack>
          </Box>
        </AuthSplitCard>

        {showOtpVerificationModal && (
          <OtpVerification
            handleShowOtpVerificationModal={handleShowOtpVerificationModal}
            forgetPassword={true}
            phoneNumber={toUsPhoneNumber(phoneNumber)}
            userRole={userRole}
            itemId={itemId}
          />
        )}
      </Box>
    </ScreenWrapper>
  );
};
export default GetOtp;
